// ============================================================================
// Time & Labor — module types and vocabularies.
//
// Package one (notional test: Ben + Antonela, then one facility). The punch
// spine lives in the DB (punch_events immutable; shift_segments derived by
// rpc_time_punch / rpc_time_correct_segment ONLY — direct writes revoked).
//
// ⛔ D-03: no punch is ever refused for a location/integrity reason anywhere
//    in this module. Every integrity failure is a flag on an accepted punch.
// ⛔ D-01: no per-worker overtime control exists here, and none may be added.
//    OT rules live INERT in labor_rules until Ben + Bryan + counsel activate.
// ============================================================================

export type GeofenceResult =
  | 'inside'
  | 'outside'
  | 'no_fence'
  | 'declined'
  | 'unavailable'
  | 'low_accuracy';

export type SegmentStatus =
  | 'open'
  | 'closed'
  | 'auto_closed'
  | 'worker_resolved'
  | 'staff_resolved'
  | 'disputed'
  | 'void';
export type ExceptionSeverity = 'high' | 'medium' | 'low';
export type ExceptionStatus = 'open' | 'reviewed' | 'auto_resolved';
export type CaptureMode =
  | 'kiosk_pin'
  | 'mobile_web'
  | 'web_staff'
  | 'supervisor_manual'
  | 'import'
  | 'system_auto_close'
  | 'worker_attested_correction';

export interface WorkerPickerRow {
  worker_id: string;
  display_name: string;
  first_name: string;
  last_name: string;
  employment_class: string;
  preferred_language: string;
  active_assignments: number;
  has_pin: boolean;
  /**
   * The market this person is a night manager for, or null (2026-09-22).
   *
   * ⛔ It is what separates William Rodriguez from the four other active
   * Rodriguezes, which is why "I still cannot find William" was a real
   * complaint about a list that did contain him. Derived from the position
   * they fill — never stored twice.
   */
  night_manager_market: string | null;
}

export interface WorkerRow {
  worker_id: string;
  first_name: string;
  last_name: string;
  preferred_name: string | null;
  phone_mobile: string | null;
  email: string | null;
  preferred_language: string;
  worker_status: 'active' | 'inactive' | 'terminated';
  employment_class: string;
  employer_provider_id: string | null;
  employee_id: string | null;
  linked_provider_id: string | null;
  work_state: string | null;
  notes: string | null;
}

export interface AssignmentRow {
  assignment_id: string;
  worker_id: string;
  facility_id: string | null;
  market_region: string | null;
  service_line_code: string;
  shift_code: string | null;
  provider_id: string | null;
  scheduled_hours_per_week: number | null;
  authorized_hours_per_week: number | null;
  /** Ben's reframe (2026-08-28): IF the scheduled span is known (8h + 1h
   *  unpaid lunch = 9h span), anything above it auto-flags. NULL = unknown,
   *  and unknown NEVER flags — the rule only fires where the answer exists. */
  scheduled_hours_per_shift: number | null;
  unpaid_break_minutes: number;
  effective_start: string;
  effective_end: string | null;
}

export interface ExceptionRow {
  exception_id: string;
  segment_id: string | null;
  punch_event_id: string | null;
  worker_id: string;
  worker_name: string;
  facility_id: string | null;
  place_display: string | null;
  market_region: string | null;
  rule_code: string;
  rule_label: string;
  spec_ref: string | null;
  severity: ExceptionSeverity;
  status: ExceptionStatus;
  detail: string | null;
  created_at: string;
  reviewed_by_eid: string | null;
  reviewed_at: string | null;
  review_note: string | null;
  started_at: string | null;
  ended_at: string | null;
  segment_status: SegmentStatus | null;
  raw_minutes: number | null;
  /** The worker's clock: the time zone of the market the building is in (markets.timezone). */
  site_tz: string;
}

export interface SegmentRow {
  segment_id: string;
  worker_id: string;
  worker_name: string;
  linked_provider_id: string | null;
  facility_id: string | null;
  place_display: string | null;
  market_region: string | null;
  service_line_code: string | null;
  service_line_label: string | null;
  shift_code: string | null;
  shift_date: string;
  started_at: string;
  ended_at: string | null;
  raw_minutes: number | null;
  break_minutes: number;
  paid_minutes: number | null;
  segment_status: SegmentStatus;
  in_event_id: string | null;
  out_event_id: string | null;
  open_exceptions: number;
  /** The worker's clock: the time zone of the market the building is in (markets.timezone). */
  site_tz: string;
}

/**
 * One punch in a night's history — the original, or something that replaced it.
 *
 * ⛔ A correction NEVER edits a punch. It inserts a new one carrying
 * `supersedes_event_id`, and the original stays exactly where it was. This is
 * the shape of reading that chain back (2026-09-22).
 */
export interface PunchHistoryRow {
  punch_event_id: string;
  event_type: 'in' | 'out' | 'break_start' | 'break_end';
  /** When the punch SAYS it happened. */
  event_at: string;
  /** When the row was written — the two differ on every correction. */
  created_at: string;
  capture_mode: CaptureMode;
  place_display: string | null;
  gps_accuracy_m: number | null;
  distance_from_site_m: number | null;
  geofence_result: string | null;
  trust_level: string | null;
  device_reported_timezone: string | null;
  supersedes_event_id: string | null;
  superseded_by: string | null;
  void_reason: string | null;
  created_by_eid: string | null;
  created_by_name: string | null;
  /** The worker's clock: the time zone of the market the building is in (markets.timezone). */
  site_tz: string;
}

/**
 * One shift that was REMOVED, and the record of who removed it (2026-09-22).
 *
 * ⛔ `hours_removed` is the RAW hours the shift had, not the paid hours —
 * removing a shift sets paid to zero, so paid hours would report every
 * removal as nothing having been removed.
 */
export interface RemovedShiftRow {
  segment_id: string;
  worker_id: string;
  worker_name: string;
  facility_id: string | null;
  market_region: string | null;
  place_display: string | null;
  service_line_code: string | null;
  shift_date: string;
  started_at: string;
  ended_at: string | null;
  hours_removed: number;
  removal_event_id: string | null;
  removed_at: string | null;
  removed_by_eid: string | null;
  removed_by_name: string | null;
  reason: string | null;
  /** The worker's clock: the time zone of the market the building is in (markets.timezone). */
  site_tz: string;
}

export interface ServiceLineRow {
  service_line_code: string;
  label: string;
  sort_order: number;
}

export interface FacilityOption {
  facility_id: string;
  /** "Menorah (Esmeralda Arita)" — owner in parentheses, always. Staff screens only. */
  display: string;
  /** "Menorah" — the building's own name, as a client sees it on their hours page. */
  name: string;
  /** The building's account manager of record — what is_my_facility() reads. */
  account_manager_eid: string | null;
}

/** One auto-closed shift awaiting the worker's answer, carried on the NEXT
 *  punch (Decision 1, 2026-08-28). The prompt is non-blocking — the punch it
 *  rides on has already been recorded — and non-dismissible: it returns on
 *  every punch until answered or deferred. */
export interface PendingAutoclose {
  exception_id: string;
  segment_id: string;
  started_at: string;
  auto_closed_at: string;
  place_display: string;
  scheduled_hours: number | null;
  raw_minutes: number | null;
  /** The worker's clock: the time zone of the market the building is in (markets.timezone). */
  site_tz: string;
}

export interface PunchResult {
  punch_event_id: string;
  segment_id: string | null;
  geofence_result: GeofenceResult | null;
  trust_level?: string;
  minutes?: number;
  suppressed_duplicate?: boolean;
  auto_voided?: boolean;
  flags?: string[];
  pending_autocloses?: PendingAutoclose[];
  /** The worker's clock for the punch just recorded. */
  site_tz?: string;
  /** Record-then-refuse: a wrong kiosk PIN RETURNS this instead of raising,
   *  so the lockout counter commits (a RAISE rolled it back — the package-one
   *  defect the worker-door smoke caught). */
  refused?: boolean;
  hint?: string;
  message?: string;
}

// ---------------------------------------------------------------------------
// The worker door (/clock, TCSD-0140) — one anon-callable RPC, four actions.
// Identity failures come back as {ok:false} payloads, never exceptions, so
// the lockout bookkeeping commits and the page keeps one calm contract.
// ---------------------------------------------------------------------------
export interface WorkerPlace {
  facility_id: string | null;
  market_region: string | null;
  service_line_code: string | null;
  shift_code: string | null;
  display: string;
}

export interface WorkerClockRefusal {
  ok: false;
  code: 'identity' | 'pin_unset' | 'pin_locked';
  message: string;
}

/** 'es' | 'en' — read from the WORKER, not from the browser. Spanish is what
 *  somebody with no preference recorded gets, and that has not changed. */
export type WorkerLang = 'es' | 'en';

export interface WorkerContext {
  ok: true;
  first_name: string;
  /** The person's own preference, so a second phone opens in their language. */
  language: WorkerLang;
  device_verified: boolean;
  /** True when this device is a building's time clock (/clock/kiosk). */
  kiosk?: boolean;
  /** Kiosk only (Ben, 1 Nov 2026): 'notice' — first time, show the photo notice; 'on';
   *  'declined' — the worker chose no photo; 'off_building' — photos are off at this building. */
  photo?: KioskPhotoMode | null;
  /** False for somebody who has never punched, which is the one moment the
   *  clock page confirms that their phone and PIN work. */
  has_ever_punched: boolean;
  on_clock: { since: string; facility_id: string | null; place_display: string; site_tz: string } | null;
  open_prompts: number;
  /** ⛔ Their assigned places, which is EMPTY for every worker today. It is no
   *  longer what the punch screen offers — the phone answers that now. */
  places: WorkerPlace[];
  /**
   * Set only for somebody filling a night-manager POSITION (item #997). Their
   * punch asks no building and shows no "not near a building" warning, in
   * either mode. The mode is the AM's setting on the position.
   */
  night_manager?: { mode: NightManagerMode; market: string | null } | null;
  /** While checked in at a building (Night + buildings): its NAME only — no
   *  times, no hours, so they can check out. Null otherwise. */
  visit?: { place_display: string } | null;
}

export type KioskPhotoMode = 'notice' | 'on' | 'declined' | 'off_building';

/** The AM's setting on a night-manager position (Ben, 2026-10-27). */
export type NightManagerMode = 'night_only' | 'night_and_buildings';

/** One building on the night manager's check-in list: their market, closest
 *  first when the phone has a fix. The distance only orders the list. */
export interface VisitOption {
  facility_id: string;
  facility_name: string;
  display: string;
  distance_m?: number | null;
}

/** One building in "Tonight — what your AM will see". */
export interface TonightVisit {
  place_display: string;
  in_at: string;
  out_at: string;
  minutes: number;
}

/** The end-of-night summary: the most recent night only, read-only, gone at
 *  the next punch-in. `between_minutes` is driving / between buildings. */
export interface TonightSummary {
  shift_date: string;
  site_tz: string;
  started_at: string;
  ended_at: string;
  worked_minutes: number;
  between_minutes: number;
  visits: TonightVisit[];
}

/** One night on the worker's own card. ⛔ Their own hours and their own rate —
 *  no building total and nobody else's anything is in this payload. */
export interface WorkerHoursRow {
  segment_id: string;
  shift_date: string;
  place_display: string;
  /** Clock times. `out_at` is null while a shift is still open. */
  in_at: string | null;
  out_at: string | null;
  hours: number;
  status: SegmentStatus;
  /** D-05: null when no VERIFIED rate covers the night. Never a zero. */
  rate: number | null;
  amount: number | null;
  rate_verified: boolean;
  open_exception: { exception_id: string; rule_code: string } | null;
  /** The worker's clock: the time zone of the market the building is in (markets.timezone). */
  site_tz: string;
}

export interface WorkerPeriod {
  start: string;
  end: string;
  rows: WorkerHoursRow[];
  hours_total: number;
  /** Null when nothing in the period is priced — the card says so in words
   *  rather than showing $0.00 (D-05). */
  amount_estimate: number | null;
  unrated_hours: number;
  /** Filled only when the whole period genuinely resolves to ONE rate. */
  rate_display: number | null;
}

export interface WorkerHours {
  ok: true;
  first_name: string;
  language: WorkerLang;
  device_verified?: boolean;
  current_period?: WorkerPeriod;
  previous_period?: WorkerPeriod;
}

/** One building the phone says the worker is standing at. */
export interface WhereFacility {
  facility_id: string;
  display: string;
  facility_name: string;
  distance_m: number;
  inside_fence: boolean;
}

/**
 * ⛔ THE PHONE ANSWERS "WHERE ARE YOU", NOT THE WORKER (2026-09-22).
 *
 * `here` carries ONE building, or the two or three that genuinely share that
 * address — 25 pairs of Active buildings sit within 150 m of each other and 11
 * of those are at identical coordinates, so GPS physically cannot separate
 * them. It is capped at three and it is never a list to scroll. When nothing
 * resolves the punch still happens, with no building on it (D-03).
 */
export interface WhereOption {
  facility_id: string;
  facility_name: string;
  display: string;
}

export type WhereAmI =
  /** The phone knows: one building, or the two or three sharing that address. */
  | { found: true; shared_address: boolean; here: WhereFacility[] }
  /** On, but they are not at work. Say where they appear to be, then ask. */
  | {
      found: false; reason: 'nothing_nearby';
      latitude: number; longitude: number;
      nearest: { facility_name: string; display: string; distance_m: number } | null;
    }
  /**
   * Off, or too rough to tell buildings apart. Ben's tree, 2026-09-22: a night
   * manager picks from THEIR market; anybody else from the buildings they work;
   * and when nothing records that — which is everybody today — from all of
   * them, with the screen saying that is why.
   */
  | {
      found: false; reason: 'no_location' | 'fix_too_rough'; accuracy_m?: number;
      scope: 'market' | 'assigned' | 'all';
      scope_label: string | null;
      options: WhereOption[];
    };

// ---------------------------------------------------------------------------
// The pay run (view four) — the ONLY view with an export at the end of it.
// Each person's nights come from the same function behind the worker's own
// card, so HR's drill-in and the worker's phone cannot disagree.
// ---------------------------------------------------------------------------
export interface PayRunPerson {
  worker_id: string;
  worker_name: string;
  section: 'W2' | '1099';
  position_title: string | null;
  position_market: string | null;
  /** Who pays them. Sub crews are paid on the weekly sub pay sheets and are
   *  deliberately not on this screen. */
  paid_by: string;
  hours_total: number;
  amount_estimate: number | null;
  rate_display: number | null;
  unrated_hours: number;
  days: WorkerHoursRow[];
}

export interface PayRunWarning {
  exception_id: string;
  rule_code: string;
  worker_id: string;
  worker_name: string;
  shift_date: string;
  started_at: string | null;
  hours: number;
  place_display: string;
  /** The worker's clock: the time zone of the market the building is in (markets.timezone). */
  site_tz: string;
}

export interface PayRunPayload {
  ok: true;
  range_start: string;
  range_end: string;
  people: PayRunPerson[];
  hours_total: number;
  amount_estimate: number | null;
  unrated_hours: number;
  warnings: PayRunWarning[];
  exported: boolean;
  /** Present only on the call that pressed Export. Opening the screen records
   *  nothing at all. */
  export: { export_id: string; version: number; supersedes: string | null; period_id: string | null } | null;
}

export interface DeviceRow {
  device_id: string;
  friendly_name: string;
  device_type: string;
  bound_facility_id: string | null;
  bound_worker_id: string | null;
  trust_tier: string;
  is_active: boolean;
  registered_by_eid: string | null;
  verified_at: string | null;
  first_seen_at: string;
  last_seen_at: string | null;
}

/** v_time_autoclose_residual — auto-closes still open where the worker has had
 *  NO punch since the exception was raised (no prompt opportunity yet). */
export interface ResidualRow {
  exception_id: string;
  segment_id: string;
  worker_id: string;
  worker_name: string;
  facility_id: string | null;
  place_display: string;
  started_at: string;
  ended_at: string | null;
  raw_minutes: number | null;
  shift_date: string;
  prompt_shown_count: number;
  created_at: string;
  /** The worker's clock: the time zone of the market the building is in (markets.timezone). */
  site_tz: string;
}

/** v_time_autoclose_coaching — the escalation cap is a VIEW, not a stored
 *  counter (Ben, Decision 1): 3+ unresolved, or 2+ in the open pay period. */
export interface CoachingRow {
  worker_id: string;
  worker_name: string;
  open_autocloses: number;
  open_this_period: number;
  latest_at: string;
}

export type ReportLinkScope = 'facility' | 'provider';

export interface ReportLinkRow {
  link_id: string;
  token: string;
  link_scope: ReportLinkScope;
  provider_id: string | null;
  label: string;
  created_by_eid: string | null;
  created_at: string;
  expires_at: string;
  revoked_at: string | null;
  /** A building link narrowed to one service line (Ben, 2026-10-23). Null = whole building. */
  service_line_code: string | null;
}

/** rpc_client_hours_report payload — HOURS ONLY, by design. One row per shift
 *  plus the real pay-period boundaries; the page computes every horizon
 *  (last 24h / week / pay period / month / trends) and both readings — by day
 *  and by person — client-side from these.
 *
 *  ⛔ No rate, amount, cost or margin may ever appear here, and the smoke walks
 *  every key in the payload to prove it rather than checking the ones somebody
 *  remembered. The clock times are what let the page be read BY DAY. */
export interface HoursReportRow {
  shift_date: string;
  facility: string;
  service_line: string;
  worker_name: string;
  in_at: string | null;
  out_at: string | null;
  hours: number;
  /** The worker's clock: the time zone of the market the building is in (markets.timezone). */
  site_tz: string;
}

export interface HoursReportPeriod {
  start: string;
  end: string;
}

// ---------------------------------------------------------------------------
// Package three — payroll generator payloads (period_exports is the record).
// ⛔ D-05: wage/amount fields are NULL wherever the rate is unverified —
//    hours always render, dollars never render unverified.
// ⛔ D-01: ot_hours is structurally 0 until the inert OT rules are activated.
// ---------------------------------------------------------------------------
export interface ExportMeta {
  export_id: string;
  version: number;
  supersedes: string | null;
  period_id: string | null;
}

export interface PaysheetSegment {
  date: string;
  in: string;
  out: string | null;
  minutes: number;
  status: SegmentStatus;
  /** The worker's clock: the time zone of the market the building is in (markets.timezone). */
  site_tz: string;
}

export interface PaysheetWorker {
  worker_id: string;
  worker_name: string;
  segments: PaysheetSegment[];
  week_hours: number;
  rate: number | null;
  rate_verified: boolean;
  amount: number | null;
}

export interface PaysheetFacility {
  place_key: string;
  place_display: string;
  facility_id: string | null;
  workers: PaysheetWorker[];
  facility_hours: number;
  total_due: number | null;
}

export interface PaysheetPayload {
  ok: boolean;
  provider_id: string;
  provider_display: string;
  week_start: string;
  week_ending: string;
  facilities: PaysheetFacility[];
  hours_total: number;
  amount_total: number | null;
  fully_priced: boolean;
  warnings: { unresolved_autocloses: number; still_open: number };
  export: ExportMeta;
}

export interface PayrollSummaryRow {
  section: 'W2' | '1099';
  worker_id: string;
  worker_name: string;
  location: string;
  reg_hours: number;
  ot_hours: number;
  wage: number | null;
  ot_wage: number | null;
  reg_pay: number | null;
  total_pay: number | null;
  rate_verified: boolean;
}

export interface GustoRow {
  first_name: string;
  last_name: string;
  earning_code: string;
  hours: number;
  location: string;
  department: string;
}

export interface PayrollSummaryPayload {
  ok: boolean;
  range_start: string;
  range_end: string;
  rows: PayrollSummaryRow[];
  gusto_rows: GustoRow[];
  hours_total: number;
  amount_total: number | null;
  export: ExportMeta;
}

export interface Contractor1099Row {
  worker_id: string;
  worker_name: string;
  location: string;
  hours: number;
  wage: number | null;
  total_pay: number | null;
  rate_verified: boolean;
  is_new: boolean;
}

export interface Contractor1099Payload {
  ok: boolean;
  range_start: string;
  range_end: string;
  rows: Contractor1099Row[];
  hours_total: number;
  amount_total: number | null;
  export: ExportMeta;
}

export type ExportType = 'sub_pay_sheet' | 'payroll_summary' | 'contractor_1099_list' | 'client_audit';

export interface ExportHistoryRow {
  export_id: string;
  export_type: ExportType;
  period_id: string | null;
  provider_id: string | null;
  version: number;
  range_start: string;
  range_end: string;
  label: string | null;
  hours_total: number | null;
  amount_total: number | null;
  created_by_eid: string | null;
  created_at: string;
}

export interface HoursReportPayload {
  ok: boolean;
  scope?: ReportLinkScope;
  scope_display?: string[];
  prepared_for?: string;
  expires_at?: string;
  generated_at?: string;
  periods?: HoursReportPeriod[];
  rows?: HoursReportRow[];
}

/** Client hint for the location capture state (D-03: both are ACCEPTED). */
export type LocationState = 'declined' | 'unavailable' | null;

// ---------------------------------------------------------------------------
// RPC hint tokens (PLAYBOOK §11): kept as DATA beside the map; the vocabulary
// test asserts coverage both ways so a renamed token cannot silently unmap.
// ---------------------------------------------------------------------------
export const RPC_HINT_TOKENS = [
  'TCS_TIME_STAFF_ONLY',
  'TCS_TIME_WORKER_UNKNOWN',
  'TCS_TIME_WORKER_SEPARATED',
  'TCS_TIME_EVENT_TYPE',
  'TCS_TIME_ALLOCATION_REQUIRED',
  'TCS_TIME_PIN_UNSET',
  'TCS_TIME_PIN_LOCKED',
  'TCS_TIME_PIN_INVALID',
  'TCS_TIME_PIN_ROLE',
  'TCS_TIME_PIN_FORMAT',
  'TCS_TIME_REASON_REQUIRED',
  'TCS_TIME_NEGATIVE_DURATION',
  'TCS_TIME_CORRECT_ROLE',
  'TCS_TIME_CORRECT_TIMES',
  'TCS_TIME_SEGMENT_UNKNOWN',
  'TCS_TIME_PERIODS_ROLE',
  'TCS_TIME_PERIOD_RANGE',
  'TCS_TIME_DEVICE_FINGERPRINT',
  'TCS_TIME_EVENT_IMMUTABLE',
  'TCS_TIME_EXPORT_IMMUTABLE',
  'TCS_TIME_RESOLUTION_KIND',
  'TCS_TIME_AUTOCLOSE_STATE',
  'TCS_TIME_USE_CORRECTION',
  'TCS_TIME_LINK_SCOPE',
  'TCS_TIME_LINK_LABEL',
  'TCS_TIME_LINK_DAYS',
  'TCS_TIME_EXPORT_ROLE',
  'TCS_TIME_EXPORT_RANGE',
  'TCS_TIME_EXPORT_SCOPE',
  'TCS_TIME_CLOCK_ACTION',
] as const;
export type RpcHintToken = (typeof RPC_HINT_TOKENS)[number];

/** Which control a refusal lands on. Anything unmapped degrades to the
 *  form-level banner (never to silence). */
export const FIELD_BY_HINT: Partial<Record<RpcHintToken, 'pin' | 'reason' | 'times' | 'worker' | 'place'>> = {
  TCS_TIME_PIN_UNSET: 'pin',
  TCS_TIME_PIN_LOCKED: 'pin',
  TCS_TIME_PIN_INVALID: 'pin',
  TCS_TIME_PIN_FORMAT: 'pin',
  TCS_TIME_REASON_REQUIRED: 'reason',
  TCS_TIME_NEGATIVE_DURATION: 'times',
  TCS_TIME_CORRECT_TIMES: 'times',
  TCS_TIME_WORKER_UNKNOWN: 'worker',
  TCS_TIME_WORKER_SEPARATED: 'worker',
  TCS_TIME_ALLOCATION_REQUIRED: 'place',
};
